import { register as registerProj4 } from 'ol/proj/proj4';
import h from 'hyperscript';

import { context } from 'cadenza/map/context/context';
import { Legend } from 'cadenza/map/legend/legend';
import { LegacyMapToolbar } from 'cadenza/map/toolbar/legacy-map-toolbar';
import { logger } from 'cadenza/utils/logging';
import { MapProperty } from 'cadenza/map/map-property';
import { MapControls } from 'cadenza/map/map-controls/map-controls';
import { proj4 } from 'cadenza/map/projection-utils';
import {
  attachToolbarAndLegend,
  stringToBoolean
} from 'cadenza/map/custom/custom-map-utils';
import { DEFAULT_MAP_CONFIG_ID } from 'cadenza/map/custom/map-constants';
import { createSelectorMap } from 'cadenza/map/custom/create-selector-map';
import { getSubnav } from 'cadenza/utils/classic-dependency-registry';
import { PrintMapAction } from 'cadenza/map/actions/print-map-action';
import { LayerProperty } from 'cadenza/map/layer-property';
import { LayerActionToolbar } from 'cadenza/map/layer-action-toolbar/layer-action-toolbar';
import { ViewProperty } from 'cadenza/map/view-property';
import { MapClientEventForwarder } from 'cadenza/map/custom/map-client-event-forwarder/map-client-event-forwarder';
import { MapSettings } from 'cadenza/map/map-settings/map-settings';
import { BlockIdentifier, getBlockSettingsSingleton } from 'cadenza/map/block-settings';
import { checkAutoStartingTimeline2dPlayer } from 'cadenza/map/actions/timeline-visualization-2d-action';
import { getControl, resolutionForScale } from 'cadenza/map/map-utils';
import { getUrlParam } from 'cadenza/utils/url-param-utils';
import { MapActionService } from 'cadenza/map/map-action-service/map-action-service';
import { CustomAppMessageType } from 'cadenza/map/custom/custom-app-message-type';
import { TOOLBAR_CONFIG } from 'cadenza/actions/actions-config';
import { NavigationHistory } from 'cadenza/map/actions/navigation-history';
import { checkAutoStartingRouting } from 'cadenza/map/actions/classic/classic-routing-action';
import { isFeatureAvailable } from 'cadenza/features';
import { ClassicMapContextApi } from 'cadenza/api-client/classic-map-context-api';

import i18n from './map.properties';

registerProj4(proj4);

const config = document.getElementById(DEFAULT_MAP_CONFIG_ID).value;

const mapId = config.map.id;
const mapTarget = document.querySelector(config.map.mapTargetSelector || '#map');

const blockSettings = getBlockSettingsSingleton();
const map = createSelectorMap(mapTarget, config);

const legend = blockSettings.isBlockEnabled(BlockIdentifier.MAP_LEGEND, config.legend) && new Legend(map, config.legend);
const toolbarConfig = { ...config.toolbar, [TOOLBAR_CONFIG.NAVIGATION_HISTORY_INSTANCE]: new NavigationHistory(map) };
const mapActionService = new MapActionService(map, toolbarConfig);
const toolbar = blockSettings.isBlockEnabled(BlockIdentifier.MAP_TOOL_BAR, config.toolbar) && new LegacyMapToolbar(map, legend, { ...toolbarConfig, mapActionService });

const mapSettings = new MapSettings(config, map);

context(map).setup()
  .then(() => {
    attachToolbarAndLegend(map, toolbar, legend, mapSettings);
    return setupMapControls(config);
  })
  .then(() => {
    setMapScale(getUrlParam({ name: 'scale' }));
    /**
     * This code should be executed only when Cadenza is used inside of a businessApp i.e. it has another 'window' to communicate with
     * Below, the forward listeners are trying to send a postMessage only based on the fact that the 'WEBAPP_INTEGRATION' is available.
     * Earlier this was also checking if we're arriving here because of a command like ShowMap, because we thought that all businessApps
     * which integrate Cadenza are opening maps using the web api commands, but this is not true.
     * ADAMAS uses Cadenza as fullscreen and embedded as well, therefore they have the 'WEBAPP_INTEGRATION' enabled in both cases.
     * They also open normal maps in the fullscreen cadenza using commands, so we really have no way of telling for sure if we're within
     * a businessApp or not. Maybe in the future we will require businessApps to answer to our 'INIT' event below, then we know there is
     * somebody on the other side and we could determine we are indeed within a businessApp, but until that happens, we will just assume
     * we're in a businessApp if the 'WEBAPP_INTEGRATION' feature is available.
     *
     */
    if (isFeatureAvailable('WEBAPP_INTEGRATION')) {
      const shouldUseMapSrs = stringToBoolean(getUrlParam({ name: 'useMapSrs' }));
      const mapClientEventForwarder = new MapClientEventForwarder(map, mapActionService, shouldUseMapSrs);
      mapClientEventForwarder.addListenerToForwardSelectedFeaturesToBusinessApp();
      mapClientEventForwarder.addListenerToForwardGazetteerAndReverseGeocodingResultsToBusinessApp();
      mapClientEventForwarder.connectApiToBusinessApp(Object.values(CustomAppMessageType.INCOMING));
    }
    checkAutoStartingTimeline2dPlayer(map);
    checkAutoStartingRouting(map);
  })
  .catch(error => {
    logger.error('Failed to setup map', error);
    if (config.mapControls !== false && !getControl(map, MapControls)) {
      map.addControl(new MapControls(config));
    }
  });

function setupMapControls (mapConfig) {
  if (mapConfig.mapControls === false) {
    return Promise.resolve();
  }
  const mapContextApi = new ClassicMapContextApi({ mapId });
  return mapContextApi.getOverviewContext().then(overviewMapContext => {
    if (mapConfig.mapControls !== false) {
      overviewMapContext.hidpi = mapConfig.map.hidpi;
      const mapControls = new MapControls(mapConfig, {
        overviewMapContext,
        srs: {
          srsTitle: map.getView().get(ViewProperty.PROJECTION_TITLE),
          srsCode: map.getView().getProjection().getCode(),
          usesDefaultSrs: map.getView().get(ViewProperty.USES_DEFAULT_SRS)
        }
      });
      map.addControl(mapControls);
    }
  });
}

window.onMapChanged = () => {
  context(map).pull();
};

// for debugging
Object.assign(mapTarget, { map, toolbar, legend });

const subnav = getSubnav();
if (subnav) {
  if (config.map.printEnabled) {
    subnav.addAction(new PrintMapAction(map));
  }

  subnav.querySelector('.d-page-title').classList.add(
    'd-page-title--normal-weight',
    'd-page-title--no-right-margin');

  if (!config.map.hideLayerControls) {
    map.on(`change:${MapProperty.SELECTED_LAYER}`, () => {
      const layer = map.get(MapProperty.SELECTED_LAYER);
      const resolution = map.getView().getResolution();
      if (layer && layer.getVisible() && context(layer).getInRange(resolution)) {
        subnav.setCenterControls(createLayerControls(layer));
      } else {
        subnav.setCenterControls(createEmptyLayerControls());
      }
    });
  }
}

function createLayerControls (layer) {
  return [
    createLayerNameElement(layer.get(LayerProperty.NAME)),
    h('.d-subnav--layer-controls', [
      new LayerActionToolbar(layer, map, {
        isInHeaderToolbar: true
      })
    ])
  ];
}

function createEmptyLayerControls () {
  return [
    createLayerNameElement(null)
  ];
}

function createLayerNameElement (layerName) {
  if (layerName) {
    return h('.page-secondary-title', layerName);
  } else {
    return h('.page-secondary-title.layer-name--empty', i18n('layer.name.empty'));
  }
}

function setMapScale (scale) {
  if (scale) {
    const view = map.getView();
    view.setResolution(resolutionForScale(view, scale)); // Set the scale after the currentExtent to maintain the center and avoid initialization problems
  }
}
